import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { labelsAPI, shipmentsAPI } from '../services/api';
import { toast } from 'react-toastify';
import { 
  FiTag, 
  FiDownload, 
  FiFile,
  FiPackage,
  FiTruck,
  FiCheckCircle,
  FiClock,
  FiAlertCircle,
  FiEye,
  FiChevronDown,
  FiChevronRight,
  FiUser,
  FiMapPin,
  FiFileText,
  FiArchive
} from 'react-icons/fi';

const Labels = () => {
  const [expandedShipments, setExpandedShipments] = useState(new Set());
  const [selectedShipments, setSelectedShipments] = useState(new Set());
  const queryClient = useQueryClient();

  const { data: masterShipments, isLoading: mastersLoading } = useQuery(
    'master-shipments',
    () => shipmentsAPI.getMasterShipments()
  );

  const toggleShipmentExpansion = (shipmentId) => {
    const newExpanded = new Set(expandedShipments);
    if (newExpanded.has(shipmentId)) {
      newExpanded.delete(shipmentId);
    } else {
      newExpanded.add(shipmentId);
    }
    setExpandedShipments(newExpanded);
  };

  const toggleShipmentSelection = (shipmentId) => {
    const newSelected = new Set(selectedShipments);
    if (newSelected.has(shipmentId)) {
      newSelected.delete(shipmentId);
    } else {
      newSelected.add(shipmentId);
    }
    setSelectedShipments(newSelected);
  };

  const handleDownloadShipmentLabels = async (shipment) => {
    try {
      const response = await labelsAPI.getLabels({ 
        master_shipment_id: shipment.id 
      });
      const labels = response.results || response;
      
      if (labels.length === 0) {
        toast.error('No labels found for this shipment');
        return;
      }

      const labelIds = labels.map(label => label.id);
      const blob = await labelsAPI.downloadLabels({ label_ids: labelIds });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `labels_${shipment.master_tracking_number}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Labels downloaded successfully');
    } catch (error) {
      toast.error('Failed to download labels');
      console.error('Error downloading labels:', error);
    }
  };

  const handleDownloadAllSelected = async () => {
    if (selectedShipments.size === 0) {
      toast.error('Please select shipments to download labels');
      return;
    }

    try {
      const allLabelIds = [];
      for (const shipmentId of selectedShipments) {
        const response = await labelsAPI.getLabels({ 
          master_shipment_id: shipmentId 
        });
        const labels = response.results || response;
        allLabelIds.push(...labels.map(label => label.id));
      }

      if (allLabelIds.length === 0) {
        toast.error('No labels found for selected shipments');
        return;
      }

      const blob = await labelsAPI.downloadLabels({ label_ids: allLabelIds });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `labels_batch_${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Downloaded ${allLabelIds.length} labels successfully`);
    } catch (error) {
      toast.error('Failed to download labels');
      console.error('Error downloading labels:', error);
    }
  };

  const handleDownloadAllLabels = async (shipment) => {
    try {
      const labels = [];
      
      // Add master label if exists
      if (shipment.label_file_url) {
        labels.push({
          url: shipment.label_file_url,
          name: `Master_${shipment.master_tracking_number}.pdf`,
          type: 'master'
        });
      }
      
      // Add box labels
      shipment.boxes?.forEach(box => {
        if (box.label_file_url) {
          labels.push({
            url: box.label_file_url,
            name: `Box_${box.box_tracking_number || box.id}.pdf`,
            type: 'box'
          });
        }
      });
      
      // Add unit labels
      shipment.boxes?.forEach(box => {
        box.units?.forEach(unit => {
          if (unit.label_file_url) {
            labels.push({
              url: unit.label_file_url,
              name: `Unit_${unit.child_tracking_number || unit.id}.pdf`,
              type: 'unit'
            });
          }
        });
      });
      
      if (labels.length === 0) {
        toast.error('No labels available for this shipment');
        return;
      }
      
      // Download all labels
      for (const label of labels) {
        const link = document.createElement('a');
        link.href = label.url;
        link.download = label.name;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Small delay between downloads
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      toast.success(`Downloaded ${labels.length} label(s)`);
    } catch (error) {
      console.error('Error downloading all labels:', error);
      toast.error('Failed to download labels');
    }
  };

  const handleDownloadBoxLabels = async (box) => {
    try {
      const labels = [];
      
      // Add box label if exists
      if (box.label_file_url) {
        labels.push({
          url: box.label_file_url,
          name: `Box_${box.box_tracking_number || box.id}.pdf`,
          type: 'box'
        });
      }
      
      // Add unit labels in this box
      box.units?.forEach(unit => {
        if (unit.label_file_url) {
          labels.push({
            url: unit.label_file_url,
            name: `Unit_${unit.child_tracking_number || unit.id}.pdf`,
            type: 'unit'
          });
        }
      });
      
      if (labels.length === 0) {
        toast.error('No labels available for this box');
        return;
      }
      
      // Download all labels
      for (const label of labels) {
        const link = document.createElement('a');
        link.href = label.url;
        link.download = label.name;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Small delay between downloads
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      toast.success(`Downloaded ${labels.length} label(s) for this box`);
    } catch (error) {
      console.error('Error downloading box labels:', error);
      toast.error('Failed to download box labels');
    }
  };

  const handleDownloadUnitLabel = async (unit) => {
    try {
      if (unit.label_file_url) {
        const link = document.createElement('a');
        link.href = unit.label_file_url;
        link.download = `Unit_${unit.child_tracking_number || unit.id}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        toast.error('No label available for this unit');
      }
    } catch (error) {
      console.error('Error downloading unit label:', error);
      toast.error('Failed to download unit label');
    }
  };

  const getShipmentStatusColor = (status) => {
    switch (status) {
      case 'delivered':
        return 'status-delivered';
      case 'in_transit':
        return 'status-in-transit';
      case 'pending':
        return 'status-pending';
      case 'exception':
        return 'status-exception';
      default:
        return 'status-pending';
    }
  };

  if (mastersLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Labels</h1>
          <p className="mt-1 text-sm text-gray-500">
            View and download labels for your shipments
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={handleDownloadAllSelected}
            disabled={selectedShipments.size === 0}
            className="flex items-center px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FiDownload className="h-4 w-4 mr-2" />
            Download Selected ({selectedShipments.size})
          </button>
        </div>
      </div>

      {/* Shipments with Labels */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Shipments with Labels
            </h3>
            <span className="text-sm text-gray-500">
              {masterShipments?.results?.filter(s => s.has_labels).length || 0} shipments with labels
            </span>
          </div>

          {masterShipments?.results?.filter(s => s.has_labels).length > 0 ? (
            <div className="overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Select
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Shipment ID / Tracking
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Labels
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Child Items
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {masterShipments.results
                    .filter(shipment => shipment.has_labels)
                    .map((shipment) => {
                      const isExpanded = expandedShipments.has(shipment.id);
                      const isSelected = selectedShipments.has(shipment.id);
                      
                      return (
                        <React.Fragment key={shipment.id}>
                          <tr className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleShipmentSelection(shipment.id)}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              <div className="flex items-center">
                                <button
                                  onClick={() => toggleShipmentExpansion(shipment.id)}
                                  className="mr-2 text-gray-400 hover:text-gray-600"
                                >
                                  {isExpanded ? (
                                    <FiChevronDown className="h-4 w-4" />
                                  ) : (
                                    <FiChevronRight className="h-4 w-4" />
                                  )}
                                </button>
                                <div>
                                  <div className="font-medium text-gray-900">
                                    {shipment.master_tracking_number}
                                  </div>
                                  {shipment.carrier_tracking_number && (
                                    <div className="text-xs text-blue-600 font-medium">
                                      Carrier: {shipment.carrier_tracking_number}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              <div className="flex items-center">
                                <FiCheckCircle className="h-4 w-4 text-green-500 mr-2" />
                                <span className="text-green-700 font-medium">
                                  {shipment.labels_count} labels
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`status-badge ${getShipmentStatusColor(shipment.status)}`}>
                                {shipment.status.replace('_', ' ')}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              <div className="flex items-center">
                                <FiPackage className="h-4 w-4 text-gray-400 mr-1" />
                                {shipment.child_shipments_count}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {new Date(shipment.created_at).toLocaleDateString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <button
                                onClick={() => handleDownloadShipmentLabels(shipment)}
                                className="text-green-600 hover:text-green-900"
                                title="Download Labels"
                              >
                                <FiDownload className="h-4 w-4" />
                              </button>
                            </td>
                          </tr>
                          {isExpanded && shipment.child_shipments && shipment.child_shipments.length > 0 && (
                            <tr>
                              <td colSpan="7" className="px-6 py-4 bg-gray-50">
                                <div className="ml-6">
                                  <h4 className="text-sm font-medium text-gray-900 mb-3">Child Shipments</h4>
                                  <div className="space-y-2">
                                    {shipment.child_shipments.map((child, index) => (
                                      <div key={child.id} className="bg-white p-3 rounded border">
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                                          <div className="flex items-center">
                                            <FiUser className="h-4 w-4 text-gray-400 mr-2" />
                                            <span className="font-medium">{child.recipient_name}</span>
                                          </div>
                                          <div className="flex items-center">
                                            <FiMapPin className="h-4 w-4 text-gray-400 mr-2" />
                                            <span className="truncate">{child.recipient_address}</span>
                                          </div>
                                          <div className="flex items-center justify-between">
                                            <span className="text-gray-500">{child.item_description}</span>
                                            <span className={`status-badge ${getShipmentStatusColor(child.status)}`}>
                                              {child.status.replace('_', ' ')}
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-6">
              <FiTag className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No labels generated yet</h3>
              <p className="mt-1 text-sm text-gray-500">
                Labels will appear here once they are generated for your shipments.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Labels;
